import React, { useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { getApiOrigin, stripWarehouseSelectionQueryParams } from '@/features/warehouse/config'
import { resolveImageUrl, type ImageLike } from '@/utils/imageUrl'

type SeoProps = {
  title: string
  description?: string | null
  image?: ImageLike | null
  url?: string
  type?: 'website' | 'product' | 'article'
  siteName?: string
  category?: string | null
  price?: number | string | null
  currency?: string
  availability?: 'in stock' | 'out of stock' | 'preorder' | 'pending'
  noindex?: boolean
  children?: React.ReactNode
}

const DEFAULT_SITE_NAME = 'NurM'
const DEFAULT_DESCRIPTION =
  'NurM — интернет-магазин техники и аксессуаров: актуальные предложения, акции и новинки.'
const FALLBACK_IMAGE_PATH = '/images/stock/stock-box.png'
const DEFAULT_CURRENCY = 'RUB'
// const DEFAULT_OG_IMAGE_WIDTH = '1200'
// const DEFAULT_OG_IMAGE_HEIGHT = '630'

const stripText = (text?: string | null) =>
  (text ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const normalizeText = (text?: string | null) =>
  (stripText(text) || DEFAULT_DESCRIPTION).slice(0, 220)

export const getSeoOrigin = () => {
  const publicSiteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || getApiOrigin()

  const envOrigin = publicSiteUrl
    ? publicSiteUrl.toString().trim()
    : ''

  if (!envOrigin) return ''

  try {
    return new URL(envOrigin).origin
  } catch {
    return envOrigin.replace(/\/$/, '')
  }
}

export const toAbsoluteUrl = (value: string, origin: string) => {
  if (!value) return origin
  if (/^https?:\/\//i.test(value)) return value
  const normalized = value.startsWith('/') ? value : `/${value}`
  return origin ? `${origin}${normalized}` : normalized
}

const normalizeAvailability = (value?: SeoProps['availability']) => {
  if (!value) return undefined
  if (value === 'in stock') return 'in stock'
  if (value === 'out of stock') return 'out of stock'
  if (value === 'preorder') return 'preorder'
  return 'pending'
}

const stripWarehouseQueryParam = (pathname: string, search: string) => {
  return `${pathname}${stripWarehouseSelectionQueryParams(search)}`
}

const formatPrice = (price?: SeoProps['price']) => {
  if (price == null) return null
  const num = typeof price === 'string' ? Number(price) : price
  if (Number.isNaN(num)) return null
  return num.toFixed(2)
}

const getImageMimeType = (url: string) => {
  const path = url.split('?')[0]?.toLowerCase() ?? ''
  if (path.endsWith('.jpg') || path.endsWith('.jpeg')) return 'image/jpeg'
  if (path.endsWith('.png')) return 'image/png'
  if (path.endsWith('.webp')) return 'image/webp'
  if (path.endsWith('.gif')) return 'image/gif'
  return ''
}

const Seo: React.FC<SeoProps> = ({
  title,
  description,
  image,
  url,
  type = 'website',
  siteName = DEFAULT_SITE_NAME,
  category,
  price,
  currency = DEFAULT_CURRENCY,
  availability,
  noindex,
  children,
}) => {
  const location = useLocation()
  const origin = useMemo(getSeoOrigin, [])
  const canonical = useMemo(() => {
    const raw = url ?? stripWarehouseQueryParam(location.pathname, location.search)
    return toAbsoluteUrl(raw, origin)
  }, [location.pathname, location.search, origin, url])

  const ogImage = useMemo(() => {
    const normalized = resolveImageUrl(image, 'original')
    const fallback = resolveImageUrl(FALLBACK_IMAGE_PATH, 'original')
    const imageUrl = normalized || fallback
    return imageUrl ? toAbsoluteUrl(imageUrl, origin) : ''
  }, [image, origin])

  const fullTitle = title ? `${title} | ${siteName}` : siteName
  const metaDescription = normalizeText(description)
  const metaCategory = stripText(category).slice(0, 120)
  const metaPrice = formatPrice(price)
  const metaAvailability = normalizeAvailability(availability)
  const robotsContent = noindex ? 'noindex, nofollow' : 'index, follow'
  const ogType = type === 'product' ? 'website' : type
  const twitterCard = ogImage ? 'summary_large_image' : 'summary'
  const ogImageType = ogImage ? getImageMimeType(ogImage) : ''

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="robots" content={robotsContent} />

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonical} />
      {ogImage ? <meta property="og:image" content={ogImage} /> : null}
      {ogImage ? <meta property="og:image:secure_url" content={ogImage} /> : null}
      {ogImageType ? <meta property="og:image:type" content={ogImageType} /> : null}
      {ogImage ? <meta property="og:image:width" /> : null}
      {ogImage ? <meta property="og:image:height" /> : null}
      {ogImage ? <meta property="og:image:alt" content={title} /> : null}
      <meta property="og:locale" content="ru_RU" />

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}

      {type === 'product' && metaPrice ? (
        <meta property="product:price:amount" content={metaPrice} />
      ) : null}
      {type === 'product' && metaPrice ? (
        <meta property="product:price:currency" content={currency} />
      ) : null}
      {type === 'product' && metaAvailability ? (
        <meta property="product:availability" content={metaAvailability} />
      ) : null}
      {type === 'product' && metaCategory ? (
        <meta property="product:category" content={metaCategory} />
      ) : null}

      <link rel="canonical" href={canonical} />
      {children}
    </Helmet>
  )
}

export default Seo
